import { updateCursorStore, useCursorStore } from '../stores/useCursorStore';
import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';

import { type GenericElement } from './element';
import { type Plugin, type PluginCursor } from './plugin';
import { type TransformHandleType } from './selection';

const RESIZE_CURSORS = ['ns', 'nesw', 'ew', 'nwse'];

export const setCursor = (cursor: PluginCursor) => {
  const resolvedCursor = resolvePluginCursor({ cursor });
  const { interactiveCanvas } = useEditorRuntimeStore.getState();

  if (!interactiveCanvas) return;

  if (useCursorStore.getState().customCursorActive) {
    interactiveCanvas.style.cursor = 'none';
  }

  interactiveCanvas.style.cursor = resolvedCursor;
};

export const toggleCustomCursor = (active: boolean) => {
  updateCursorStore({ customCursorActive: active });
};

const rotateResizeCursor = (cursor: string, angle: number) => {
  const index = RESIZE_CURSORS.indexOf(cursor);
  if (index >= 0) {
    const a = Math.round(angle / (Math.PI / 4));
    cursor = RESIZE_CURSORS[(index + a) % RESIZE_CURSORS.length];
  }
  return cursor;
};

export const getCursorForResizingElement = (
  transformHandleType: TransformHandleType,
  element?: GenericElement,
): string => {
  const shouldSwapCursors =
    element && Math.sign(element.height) * Math.sign(element.width) === -1;

  let cursor = null;

  switch (transformHandleType) {
    case 'e':
    case 'w':
      cursor = 'ew';
      break;
    case 'n':
    case 's':
      cursor = 'ns';
      break;
    case 'ne':
    case 'sw':
      if (shouldSwapCursors) {
        cursor = 'nwse';
      } else {
        cursor = 'nesw';
      }
      break;
    case 'nw':
    case 'se':
      if (shouldSwapCursors) {
        cursor = 'nesw';
      } else {
        cursor = 'nwse';
      }
      break;
    case 'rotation':
      return 'grab';
  }

  if (cursor && element) {
    cursor = rotateResizeCursor(cursor, element.angle);
  }

  return cursor ? `${cursor}-resize` : '';
};

export const resolvePluginCursor = (
  plugin: Pick<Plugin, 'cursor'> | undefined,
): string => {
  if (!plugin) return 'auto';
  if (typeof plugin.cursor === 'function') return plugin.cursor() ?? 'default';
  return plugin.cursor;
};

/** Only resolves dynamic cursors. Not ones that are statically defined in the plugin. */
export const resolveDynamicPluginCursor = (
  plugin: Pick<Plugin, 'cursor'> | undefined,
): string | null => {
  if (!plugin) return null;
  if (typeof plugin.cursor !== 'function') return null;
  return plugin.cursor() ?? null;
};
